import { DateTime } from "luxon";


/**
 * Funkcja sprawdzająca czy podana data jest poprawna oraz czy jest w formacie DD/MM/YYYY
 * > Dla "DD" możemy wpisać dni od 01 do 31 
 * > Dla "MM" możemy wpisać miesiące od 01 do 12
 * > Dla "YYYY" możemy wpisać rok od 1900 do 2199
 * @param dateToCheck 
 * @returns 
 */
export const isDateValid = {
   name: 'isDateValid',
   message: 'Wpisz datę w formacie DD/MM/YYYY',
   test: (dateToCheck: string | undefined) => {
      if(dateToCheck?.length){
         const dateRegexp = /^(0[1-9]|1\d|2\d|3[01])\/(0[1-9]|1[0-2])\/(19|20|21)\d{2}$/g
         
         if(dateRegexp.test(dateToCheck)){
             return Boolean(DateTime.fromFormat(dateToCheck, 'dd/MM/yyyy').isValid)
         }
         else {
            return false
         }
      }
      else {
         return false
      }
   }
}

/**
 * Funkcja pomocnicza. Pozwala na współdzielenie logiki między dwoma funkcjami: isPersonalIdReleaseCorrect oraz isPersonalIdValidityCorrect
 */

const isPersonalIdReleaseCorrectHelper = (personalIdReleaseDate: string | undefined): boolean => {
   if(personalIdReleaseDate?.length){
      const currentDate = DateTime.now()
      const dateToValid = DateTime.fromFormat(personalIdReleaseDate, 'dd/MM/yyyy')


      return (
         dateToValid.isValid 
         && currentDate.minus({ years: 11 }).startOf('day') < dateToValid.startOf('day')  // Czy nie jest większa niż 11 lat od daty złożenia wniosku
         && currentDate.startOf('day') >= dateToValid.startOf('day')                      // Czy nie jest większa od aktualnej daty
      ) 
   }
   else {
      return false
   }
}

/**
 * Funkcja pomocnicza. Zwraca informację czy data jest datą wcześniejszą czy późniejszą od aktualnej daty
 * @param dateToCheck  - Data do sprawdzenia
 * @param type - Wartość 'earlier' lub 'future' - jeśli 'feature' to walidujemy czy data jest PÓŹNIEJSZA od dzisiejszej
 *                                                jeśli 'earlier' to walidujemy czy data jest WCZEŚNIEJSZA od dzisiejszej         
 * @returns { Boolean }
 */
const isDateFutureOrElierHelper = (dateToCheck: string | undefined, type: 'earlier' | 'future'): boolean => {
   if(dateToCheck?.length ) {
      const currentDate = DateTime.now()
      const dateToValid = DateTime.fromFormat(dateToCheck, 'dd/MM/yyyy')

      if(dateToValid.isValid){
        switch(type){
            case 'earlier': {
               return currentDate.startOf('day') < dateToValid.startOf('day') // Czy jest mniejsza niż aktualna data
            }
            case 'future': {
               return currentDate.startOf('day') > dateToValid.startOf('day')  // Czy jest większa niż aktualna data
            }
            default: {
               return false
            }
        }
      }
      else {
         return false
      }
   }
   else {
      return false
   }
}

/**
   Funkcja sprawdzająca czy data wydania dowodu osobistego podana przez użytkwonika,
   nie jest większa niż 11 lat od daty złożenia wniosku (aktualnej daty i godziny)
   oraz czy nie jest większa od aktualnej daty.
*/
export const isPersonalIdReleaseCorrect = {
   name: 'isPersonalIdReleaseCorrect',
   message: 'Nieprawidłowa wartość',
   test: (dateToCheck: string | undefined) => {
      return isPersonalIdReleaseCorrectHelper(dateToCheck)
   },
 }



/**
   Funkcja sprawdzająca czy data ważności dowodu osobistego podana przez użytkwonika,
   jest co najmniej 4 lata większa niż data wydania dowodu oraz czy jest większa niż 
   data złożenia wniosku (aktualna data)
*/
export const isPersonalIdValidityCorrect = {
   name: 'isPersonalIdValidityCorrect',
   message: 'Nieprawidłowa wartość',
   test: (dateToCheck: string | undefined, context: any) => {

      const { PersonalIdRelease } = context.parent
      const isPersonalIdReleaseValid = isPersonalIdReleaseCorrectHelper(PersonalIdRelease) //Sprawdzamy czy data wydania dowodu jest poprawna

      if(PersonalIdRelease === undefined){ //Jeśli "data wydania dowodu osobistego" nie jest uzupełniona, zwracamy inny komunikat błędu
         return context.createError({
            message: 'Uzupełnij datę wydania jako pierwszą',
            path: context.path
         })
      }

      if(dateToCheck?.length && isPersonalIdReleaseValid) {
         const currentDate = DateTime.now()
         const personalIdReleaseDate = DateTime.fromFormat(PersonalIdRelease, 'dd/MM/yyyy') // Data wydania dowodu osobistego
         const dateToValid = DateTime.fromFormat(dateToCheck, 'dd/MM/yyyy')

         return (
            dateToValid.isValid
            && currentDate.startOf('day') < dateToValid.startOf('day')  // Czy jest większa niż aktualna data
            && dateToValid.startOf('day') > personalIdReleaseDate.plus({ years: 4 }).startOf('day')  //Czy jest co najmniej 4 lata większa niż data wydania dowodu
         )
      }
      else {
         return false
      }
   },
 }

/**
   Funkcja sprawdzająca czy podana data nie jest datą dzisiejszą, bądź PÓŹNIEJSZĄ od dzisiejszej 
   (nie jest datą z przyszłości).
*/
export const isNotFutureDate = {
   name: 'isNotFutureDate',
   message: 'Nieprawidłowa wartość',
   test: (dateToCheck: string | undefined) => {
      return isDateFutureOrElierHelper(dateToCheck, 'future')
   },
} 

/**
   Funkcja sprawdzająca czy podana data nie jest datą dzisiejszą, bądź WCZEŚNIEJSZĄ od dzisiejszej 
   (nie jest datą z przyszłości).
*/
export const isNotEarlierDate = {
   name: 'isNotEarlierDate',
   message: 'Nieprawidłowa wartość',
   test: (dateToCheck: string | undefined) => {
      return isDateFutureOrElierHelper(dateToCheck, 'earlier')
   },
} 